import { SKILL_LEVEL_MAX } from './constants';
import { getWieldedLoadout } from './equipped-items';
import { findSkillCatalogEntry, SKILL_CATALOG, type SkillStatWeight } from './skill-catalog';
import { DEEP_POOL_CAPACITY_BONUS, hasSkillPerk } from './skill-perks';
import { clamp } from './utils';
import type {
    Character,
    CoreStats,
    CharacterAttributes,
    CharacterEquipmentLoadout,
    CharacterEquipmentType,
    Equipment,
    Skill,
    SkillActivation,
    SkillCategory,
    SkillDecayProfile,
    SkillLevel,
} from './types/game';

export interface SkillDefinition {
    id: string;
    name: string;
    focus?: Skill['focus'];
    category: SkillCategory;
    activation: SkillActivation;
    decayProfile: SkillDecayProfile;
}

export interface ResourcePool {
    max: number;
    current: number;
}

export interface ResourcePools {
    essence: ResourcePool;
}

export interface CoreStatsAttributeWeight {
    id: string;
    value: number;
    weight: number;
}

export interface CoreStatsSkillWeight {
    id: string;
    level: number;
    weight: number;
}

/**
 * The weight in core stat formulas of each strength of "Stats fed": a primary
 * (+++) skill counts fully, a secondary (++) one 0.6, a minor (+) one 0.25.
 * First-pass numbers to tune.
 */
export const SKILL_STAT_WEIGHT_VALUES: Readonly<Record<SkillStatWeight, number>> = { 3: 1, 2: 0.6, 1: 0.25 };

function roundToNearestTen(value: number): number {
    return Math.round(value / 10) * 10;
}

/**
 * True when an equipment slot actually holds a real item, rather than being
 * empty or unset. Slots are never `null`/`undefined` in persisted data -
 * an empty slot is represented by a placeholder item literally named
 * `"empty"` - so this checks both the JS-level absence and that sentinel.
 * This is the single canonical equipped-item check; every other copy of
 * this predicate across the workspace should import this one instead of
 * reimplementing the `name.trim().toLowerCase() !== 'empty'` check.
 */
export function isEquippedItem(item: Equipment | null | undefined): item is Equipment {
    return item !== null && item !== undefined && item.name.trim().toLowerCase() !== 'empty';
}

/**
 * How many Essence skills count toward Conviction. Essence skills are always
 * active, so without a limit every one of them would stack; martial stats
 * are limited by the weapon in hand instead. Two keeps Conviction on the
 * same curve as Melee (about x1.16 fresh, x2.6 fully trained).
 */
export const CONVICTION_SKILL_COUNT = 2;

/** The `count` skills contributing most (level times weight). */
function strongestSkills(skills: readonly CoreStatsSkillWeight[], count: number): CoreStatsSkillWeight[] {
    return [...skills].sort((a, b) => b.level * b.weight - a.level * a.weight).slice(0, count);
}

function equipBonus(attr: string, equipped: CharacterEquipmentLoadout): number {
    const allEquipped = [
        ...equipped.weapons,
        equipped.armor,
        ...equipped.miscellaneous,
    ].filter(isEquippedItem);

    return allEquipped.reduce((total, item) => total + ((item.bonuses as Record<string, number | undefined>)[attr] ?? 0), 0);
}

function hasEquipmentType(
    equipped: CharacterEquipmentLoadout,
    equipmentType: CharacterEquipmentType
): boolean {
    return equipped.weapons.some((weapon) => isEquippedItem(weapon) && weapon.equipmentType === equipmentType);
}

function resolveEssencePoolBonus(equipped: CharacterEquipmentLoadout): number {
    const allEquipped = [
        ...equipped.weapons,
        equipped.armor,
        ...equipped.miscellaneous,
    ].filter(isEquippedItem);

    return allEquipped.reduce((total, item) => {
        const next = item.bonuses.essencePool ?? 0;
        return total + next;
    }, 0);
}

/** Praxis for the next level on the normal climb; see `resolvePraxisToAdvance` for a given skill. */
export function resolveBasePraxisToAdvance(level: SkillLevel): number {
    if (level >= SKILL_LEVEL_MAX) {
        return 0;
    }

    const exactCost = 100 * Math.log2(level + 1);
    return roundToNearestTen(exactCost);
}

/**
 * Praxis the skill needs for its next level. A skill that has been mastered
 * (reached 10) and slipped to 9 retrains for half the normal climb
 * (docs/gameplay/skill-definitions.md, Decay).
 */
export function resolvePraxisToAdvance(skill: Pick<Skill, 'level' | 'peakLevel'>): number {
    const cost = resolveBasePraxisToAdvance(skill.level);
    const isMasteryRetrain = skill.level === SKILL_LEVEL_MAX - 1 && (skill.peakLevel ?? skill.level) >= SKILL_LEVEL_MAX;
    return isMasteryRetrain ? roundToNearestTen(cost * MASTERY_RETRAIN_PRAXIS_FRACTION) : cost;
}

/** All the praxis a skill at `level` with `praxis` toward the next has earned, counted from level 1. */
export function resolveCumulativePraxis(level: number, praxis: number): number {
    let total = Math.max(0, praxis);
    for (let earlier = 1; earlier < level; earlier += 1) {
        total += resolveBasePraxisToAdvance(earlier as SkillLevel);
    }
    return total;
}

/** Weight of each of the last five runs in recent training, newest first (docs/gameplay/skill-tree-ui.md, Links). */
export const RECENT_TRAINING_RUN_WEIGHTS: readonly number[] = [1, 0.8, 0.6, 0.4, 0.2];

/**
 * Recent training by skill id: the praxis each skill gained over the last
 * runs (`runs`, newest first, each a praxis-by-skill map), newer runs
 * weighted more. Drives the skill tree's link pulses.
 */
export function resolveRecentTraining(runs: ReadonlyArray<Readonly<Record<string, number>>>): Record<string, number> {
    const totals: Record<string, number> = {};
    runs.slice(0, RECENT_TRAINING_RUN_WEIGHTS.length).forEach((run, index) => {
        for (const [skillId, praxis] of Object.entries(run)) {
            totals[skillId] = (totals[skillId] ?? 0) + praxis * RECENT_TRAINING_RUN_WEIGHTS[index]!;
        }
    });
    return totals;
}

/** How close a skill is to losing a level from disuse. `none` for skills that never decay (the Essence branch). */
export type SkillDecayRisk = 'none' | 'low' | 'moderate' | 'high';

/** Inactive ticks before a level-1 skill decays, by decay profile; each level above 1 adds `DECAY_LEVEL_SCALAR_TICKS`. */
const DECAY_TICK_THRESHOLD_BY_PROFILE: Record<Exclude<SkillDecayProfile, 'none'>, number> = {
    fast: 900,
    'medium-fast': 1200,
    medium: 1800,
    slow: 2700,
};

const DECAY_LEVEL_SCALAR_TICKS = 60;

/**
 * Martial milestones decay stops at: a skill that has reached one of these
 * levels never decays below it, so the perks earned there stay earned.
 * Mastery (10) is not one: a mastered skill can slip to 9.
 */
export const SKILL_DECAY_FLOOR_LEVELS: readonly SkillLevel[] = [3, 6];

/** Share of the normal 9 → 10 praxis a mastered skill needs to retrain after slipping to 9. */
export const MASTERY_RETRAIN_PRAXIS_FRACTION = 0.5;

/** The lowest level decay can take the skill to: the highest milestone its peak level has reached, else 1. */
export function resolveSkillDecayFloor(skill: Pick<Skill, 'level' | 'peakLevel'>): SkillLevel {
    const peakLevel = Math.max(skill.peakLevel ?? skill.level, skill.level);
    return SKILL_DECAY_FLOOR_LEVELS.reduce<SkillLevel>((floor, milestone) => (peakLevel >= milestone ? milestone : floor), 1);
}

/** Whether decay can still take anything from the skill: it decays at all and has praxis or levels above its floor. */
export function canSkillDecay(skill: Pick<Skill, 'level' | 'peakLevel' | 'praxis' | 'decayProfile'>): boolean {
    if ((skill.decayProfile ?? 'medium') === 'none') {
        return false;
    }
    return skill.praxis > 0 || skill.level > resolveSkillDecayFloor(skill);
}

/** Inactive ticks before the skill loses a point of praxis or a level; `undefined` for a skill that never decays. */
export function resolveSkillDecayThreshold(skill: Pick<Skill, 'level' | 'decayProfile'>): number | undefined {
    const decayProfile = skill.decayProfile ?? 'medium';
    if (decayProfile === 'none') {
        return undefined;
    }
    return DECAY_TICK_THRESHOLD_BY_PROFILE[decayProfile] + (skill.level - 1) * DECAY_LEVEL_SCALAR_TICKS;
}

/**
 * Rates a skill's decay risk from its inactive ticks: high from 80% of its
 * threshold, moderate from 40%. `none` when decay can't take anything (an
 * Essence skill, or a Martial skill resting at its milestone floor).
 */
export function resolveSkillDecayRisk(skill: Skill): SkillDecayRisk {
    const threshold = resolveSkillDecayThreshold(skill);
    if (threshold === undefined || !canSkillDecay(skill)) {
        return 'none';
    }

    const ratio = threshold > 0 ? skill.inactiveTicks / threshold : 0;
    if (ratio >= 0.8) {
        return 'high';
    }
    if (ratio >= 0.4) {
        return 'moderate';
    }
    return 'low';
}

export function isSkillActive(skill: Skill, equipped: CharacterEquipmentLoadout): boolean {
    const activation = skill.activation ?? 'always';

    if (activation === 'always') {
        return true;
    }

    if (activation === 'requires-dual') {
        const offHand = equipped.weapons[1];
        return (
            isEquippedItem(equipped.weapons[0]) &&
            isEquippedItem(offHand) &&
            offHand.equipmentType !== 'shield'
        );
    }

    if (activation === 'requires-shield') {
        return isEquippedItem(equipped.weapons[1]) && equipped.weapons[1].equipmentType === 'shield';
    }

    if (activation === 'requires-unarmed') {
        return !equipped.weapons.some((weapon) => isEquippedItem(weapon) && weapon.equipmentType !== 'shield');
    }

    if (activation === 'requires-melee') {
        return hasEquipmentType(equipped, 'melee');
    }

    return hasEquipmentType(equipped, 'range');
}

/** Fills a skill's definition fields (focus, category, activation, decay) from the skill catalog when they are missing. */
export function normalizeSkill(skill: Skill): Skill {
    const definition = findSkillCatalogEntry(skill.id);
    return {
        ...skill,
        focus: skill.focus ?? definition?.focus,
        category: skill.category ?? definition?.category,
        activation: skill.activation ?? definition?.activation,
        decayProfile: skill.decayProfile ?? definition?.decayProfile,
        peakLevel: Math.max(skill.peakLevel ?? skill.level, skill.level) as SkillLevel,
    };
}

interface ResolveCoreStatComponentParams {
    base?: number;
    primaryAttribute: CoreStatsAttributeWeight;
    secondaryAttributes?: CoreStatsAttributeWeight[];
    skills?: CoreStatsSkillWeight[];
    equipmentFlatBonus?: number;
    minResult?: number;
    maxResult?: number;
}

/**
 * Weighted-sum scoring primitive used by `resolveCoreStats` to derive one
 * core stat (e.g. melee) from a primary attribute, secondary attributes,
 * and active skill levels. Not exported - only meaningful as a building
 * block for the six-stat formula in `resolveCoreStats`.
 */
/**
 * Derives a single core stat (melee, range, conviction, etc.) from attributes,
 * skill levels, and equipment bonuses.
 *
 * Formula:
 *   attributeScore = base + (primaryAttribute × weight) + (secondaryAttributes × weights)
 *   skillMultiplier = 1 + sum(skillLevel × weight × 0.08)
 *   result = clamp(attributeScore × skillMultiplier + equipmentBonus, min, max)
 *
 * Key insight: Skills are weighted 2× heavier than attributes in the player-power
 * formulas (player-power.ts). This function uses 0.08 per skill level; the
 * player-power formulas use 0.02 per attribute and 0.02 per skill level (2:1 ratio).
 * This ensures specialization in skills early-game beats stat-only builds.
 *
 * Note: Base attributes range from 2 (starting) to 10 (hard capped). Attributes
 * scale via Veil Attunement Mastery milestones (5, 10, 15, 20, 30, 40, 50, 65, 80, 100%).
 * This function treats attributes as opaque values and does not depend on the specific
 * range; the weights and skill multiplier scale correctly across 2-10.
 */
function resolveCoreStatComponent(params: ResolveCoreStatComponentParams): number {
    const {
        base = 5,
        primaryAttribute,
        secondaryAttributes = [],
        skills = [],
        equipmentFlatBonus = 0,
        minResult = 1,
        maxResult = 100,
    } = params;

    const primaryScore = primaryAttribute.value * primaryAttribute.weight;
    const secondaryScore = secondaryAttributes.reduce((sum, attribute) => {
        return sum + attribute.value * attribute.weight;
    }, 0);

    const attributeScore = base + primaryScore + secondaryScore;
    const skillMultiplier =
        1 +
        skills.reduce((sum, skill) => {
            return sum + skill.level * skill.weight * 0.08;
        }, 0);

    const rawScore = attributeScore * skillMultiplier + equipmentFlatBonus;
    return clamp(Math.trunc(rawScore), minResult, maxResult);
}

/**
 * Derives all six core stats (melee, range, conviction, dodge, speed, resistance)
 * from base attributes, active skill levels, and equipped item bonuses.
 *
 * Attributes:
 * - Base attributes (vitality, strength, agility, quickness, intellect, endurance)
 *   start at 2 and scale to 10 (hard capped) via Veil Attunement Mastery.
 * - Veil Attunement awards +1 attribute point at milestones: 5%, 10%, 15%, 20%,
 *   30%, 40%, 50%, 65%, 80%, 100% (total 10 points from Mastery).
 * - Each core stat has a primary attribute (weight 1.0) and secondary attributes
 *   (weight 0.25–0.75) that contribute to its final value.
 *
 * Skills:
 * - Only active skills contribute (determined by equipment and skill activation rules).
 * - Skill levels are weighted by effectiveness (1.0 for primary, 0.1–0.85 for secondary).
 * - Skills advance through levels 1-10 by earning praxis and decay with inactivity.
 * - Veil Attunement (Essence branch root) does NOT feed into core stats directly;
 *   it provides attribute points instead. See character-attributes-and-stats-plan.md.
 *
 * Equipment:
 * - Flat bonuses (+N to a core stat) are added after attribute/skill derivation.
 * - Modifiers like 'melee', 'range', 'conviction', 'dodge', 'speed', 'resistance' stack
 *   from all equipped items (weapons, armor, rings, amulets).
 *
 * Balance:
 * - Attributes contribute via weighted sum (primaryAttribute + secondaryAttributes).
 * - Skills contribute via multiplicative 0.08 per level per weight (8% per point).
 * - A character with high attributes but low skills will have weak derived stats.
 * - A character with high skills but low attributes will have weak derived stats.
 * - This forces meaningful progression in both attribute (Veil Attunement) and
 *   skill systems for competitive combat power.
 * - Early-game Combat specialists outpace Essence specialists through skill leveling.
 * - Mid-game Essence specialists catch up (first 4 Mastery points by 20%).
 * - Late-game specialization matters: pure specialists max one system but plateau
 *   in others, while balanced characters have moderate strength everywhere.
 */
export function resolveCoreStats(
    attributes: CharacterAttributes,
    skills: Skill[],
    equipped: CharacterEquipmentLoadout
): CoreStats {
    const normalized = skills.map(normalizeSkill);
    const skillById = new Map(normalized.map((skill) => [skill.id, skill]));

    const getActiveSkillLevel = (id: string): number => {
        const skill = skillById.get(id);
        if (!skill) {
            return 0;
        }

        if (!isSkillActive(skill, equipped)) {
            return 0;
        }

        return skill.level;
    };

    // Each skill feeds the stats its definition names (SKILL_CATALOG `stats`).
    const skillsFeeding = (stat: keyof CoreStats): CoreStatsSkillWeight[] => SKILL_CATALOG.flatMap((entry) => {
        const weight = entry.stats[stat];
        return weight ? [{ id: entry.id, level: getActiveSkillLevel(entry.id), weight: SKILL_STAT_WEIGHT_VALUES[weight] }] : [];
    });

    return {
        melee: resolveCoreStatComponent({
            primaryAttribute: { id: 'strength', value: attributes.strength, weight: 1.0 },
            secondaryAttributes: [
                { id: 'agility', value: attributes.agility, weight: 0.5 },
                { id: 'quickness', value: attributes.quickness, weight: 0.35 },
            ],
            skills: skillsFeeding('melee'),
            equipmentFlatBonus: equipBonus('melee', equipped),
        }),
        range: resolveCoreStatComponent({
            primaryAttribute: { id: 'agility', value: attributes.agility, weight: 1.0 },
            secondaryAttributes: [
                { id: 'quickness', value: attributes.quickness, weight: 0.5 },
            ],
            skills: skillsFeeding('range'),
            equipmentFlatBonus: equipBonus('range', equipped),
        }),
        conviction: resolveCoreStatComponent({
            primaryAttribute: { id: 'intellect', value: attributes.intellect, weight: 1.0 },
            secondaryAttributes: [
                { id: 'endurance', value: attributes.endurance, weight: 0.75 },
                { id: 'agility', value: attributes.agility, weight: 0.25 },
            ],
            skills: strongestSkills(skillsFeeding('conviction'), CONVICTION_SKILL_COUNT),
            equipmentFlatBonus: equipBonus('conviction', equipped),
        }),
        dodge: resolveCoreStatComponent({
            primaryAttribute: { id: 'agility', value: attributes.agility, weight: 1.0 },
            secondaryAttributes: [
                { id: 'quickness', value: attributes.quickness, weight: 0.55 },
            ],
            skills: skillsFeeding('dodge'),
            equipmentFlatBonus: equipBonus('dodge', equipped),
        }),
        speed: resolveCoreStatComponent({
            primaryAttribute: { id: 'quickness', value: attributes.quickness, weight: 1.0 },
            secondaryAttributes: [
                { id: 'agility', value: attributes.agility, weight: 0.4 },
                { id: 'strength', value: attributes.strength, weight: 0.2 },
            ],
            skills: skillsFeeding('speed'),
            equipmentFlatBonus: equipBonus('speed', equipped),
        }),
        resistance: resolveCoreStatComponent({
            primaryAttribute: { id: 'vitality', value: attributes.vitality, weight: 0.75 },
            secondaryAttributes: [
                { id: 'endurance', value: attributes.endurance, weight: 0.75 },
                { id: 'intellect', value: attributes.intellect, weight: 0.25 },
            ],
            skills: skillsFeeding('resistance'),
            equipmentFlatBonus: equipBonus('resistance', equipped),
        }),
    };
}

/**
 * Resolves a character's effective `CoreStats`: the precomputed
 * `character.coreStats` if present, otherwise derived from
 * `attributes`/`equipped` (some network payloads carry a precomputed
 * `coreStats`, others only raw attributes/equipment to derive it from).
 *
 * `skills` defaults to `character.skills ?? []` when not passed explicitly -
 * a character with attributes and equipment but no skills still gets real
 * derived stats (skills simply contribute 0), rather than being treated as
 * having no resolvable stats at all. Pass an already-normalized `skills`
 * array explicitly to avoid `resolveCoreStats` re-normalizing it internally
 * when the caller already has one on hand (e.g. `resolveAttackTiming` reuses
 * the same normalized array for both skill-level and core-stat resolution).
 *
 * Returns `undefined` only when there is nothing to derive from at all
 * (`attributes` or `equipped` missing).
 */
export function resolveEffectiveCoreStats(character: Character, skills?: Skill[]): CoreStats | undefined {
    if (character.coreStats) {
        return character.coreStats;
    }

    const loadout = getWieldedLoadout(character);
    if (character.attributes && loadout) {
        return resolveCoreStats(character.attributes, skills ?? character.skills ?? [], loadout);
    }

    return undefined;
}

/**
 * The essence pool before attributes and gear. There is no passive
 * regeneration: a character recovers with Draw. A new character's pool plus
 * Draw's restore cast on cooldown over two minutes is about one enemy
 * damage-rating unit (100, see npc-damage-rating.ts), so a rating-1 enemy
 * fells one in about that long.
 */
export const BASE_ESSENCE_POOL = 62;

export function resolveResourcePools(
    attributes: CharacterAttributes,
    equipped?: CharacterEquipmentLoadout,
    currentEssence?: number,
    skills?: readonly Skill[]
): ResourcePools {
    const essencePoolBonus = equipped ? resolveEssencePoolBonus(equipped) : 0;
    // Deep Pool (Attune mastery) widens the whole pool.
    const capacityFactor = hasSkillPerk(skills, 'deep-pool') ? 1 + DEEP_POOL_CAPACITY_BONUS : 1;
    const max = Math.max(20, Math.round((BASE_ESSENCE_POOL + attributes.intellect * 8 + attributes.endurance * 5 + essencePoolBonus) * capacityFactor));
    const current = currentEssence !== undefined ? Math.min(currentEssence, max) : max;

    return {
        essence: {
            max,
            current,
        },
    };
}
